import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { SkeletonBlock, SkeletonPageShell, SkeletonRow, SkeletonStack } from 'src/components/widgets/skeleton';

const ProductConfigurationSkeletonShell = styled(SkeletonPageShell).attrs({ $padding: '0' })`
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: #fafafa;
`;

const ProductConfigurationSkeletonMain = styled.main`
    flex: 1;
    display: flex;
    justify-content: center;
    min-width: 0;
`;

// Mirrors addProduct.tsx PageContainer (source-and-type selection page).
const ProductConfigurationSkeletonContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 40px;
    width: 100%;
    max-width: 920px;
    padding: 24px 24px 80px;
    min-width: 0;

    @media (max-width: 760px) {
        padding: 24px 16px 80px;
    }
`;

const ProductConfigurationSkeletonHeader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
`;

// Tab list: three equal tabs.
const ProductConfigurationSkeletonTabs = styled.div`
    display: flex;
    width: 100%;
    gap: 0;
    border-bottom: 1px solid #eef1f3;
`;

const ProductConfigurationSkeletonTab = styled.div<{ $active?: boolean }>`
    flex: 1 1 0%;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 8px;
    border-bottom: 2px solid ${(props) => (props.$active ? props.theme.skeleton.foregroundColor : 'transparent')};
`;

// Selected tab panel: description box + primary action. Reserves the same min-height as the real page.
const ProductConfigurationSkeletonPanel = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 360px;
`;

const ProductConfigurationSkeletonPanelAction = styled.div`
    display: flex;
    justify-content: center;
    padding-top: 24px;
`;

const ProductConfigurationSkeletonFaqList = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid #eef1f3;
    border-radius: 10px;
    overflow: hidden;
`;

const ProductConfigurationSkeletonFaqRow = styled.div`
    padding: 15px 20px;
    background: #f9fafb;

    &:not(:last-child) {
        border-bottom: 1px solid #eef1f3;
    }
`;

const FAQ_ROW_WIDTHS = ['320px', '360px', '420px', '300px', '340px', '380px'];

const AddProductConfigurationSkeleton: FC = () => (
    <ProductConfigurationSkeletonShell aria-hidden='true'>
        <ProductConfigurationSkeletonMain>
            <ProductConfigurationSkeletonContainer>
                {/* Header */}
                <ProductConfigurationSkeletonHeader>
                    <SkeletonBlock $width='min(320px, 90%)' $height='24px' />
                    <SkeletonBlock $width='min(440px, 100%)' $height='16px' />
                </ProductConfigurationSkeletonHeader>

                {/* Tabs + selected panel */}
                <div>
                    <ProductConfigurationSkeletonTabs>
                        {[0, 1, 2].map((i) => (
                            <ProductConfigurationSkeletonTab key={i} $active={i === 0}>
                                <SkeletonBlock $width='96px' $height='16px' />
                                <SkeletonBlock $width='72px' $height='18px' $radius='10px' />
                            </ProductConfigurationSkeletonTab>
                        ))}
                    </ProductConfigurationSkeletonTabs>

                    <ProductConfigurationSkeletonPanel>
                        <SkeletonBlock $height='90px' $radius='10px' />
                        <ProductConfigurationSkeletonPanelAction>
                            <SkeletonBlock $width='280px' $height='44px' $radius='8px' />
                        </ProductConfigurationSkeletonPanelAction>
                    </ProductConfigurationSkeletonPanel>
                </div>

                {/* FAQ */}
                <SkeletonStack $gap='16px'>
                    <SkeletonBlock $width='240px' $height='18px' />
                    <ProductConfigurationSkeletonFaqList>
                        {FAQ_ROW_WIDTHS.map((width, i) => (
                            <ProductConfigurationSkeletonFaqRow key={i}>
                                <SkeletonRow $gap='12px'>
                                    <SkeletonBlock $width={width} $height='15px' />
                                </SkeletonRow>
                            </ProductConfigurationSkeletonFaqRow>
                        ))}
                    </ProductConfigurationSkeletonFaqList>
                </SkeletonStack>
            </ProductConfigurationSkeletonContainer>
        </ProductConfigurationSkeletonMain>
    </ProductConfigurationSkeletonShell>
);

export const ProductConfigurationSkeleton: FC = () => {
    const { page } = useParams();
    if (page !== 'variations') return <AddProductConfigurationSkeleton />;

    return (
        <SkeletonPageShell aria-hidden='true'>
            <SkeletonStack $gap='24px'>
                <SkeletonBlock $width='280px' $height='24px' />
                <SkeletonRow $gap='12px'>
                    <SkeletonBlock $width='220px' $height='40px' />
                    <SkeletonBlock $width='100px' $height='40px' />
                    <SkeletonBlock $width='140px' $height='40px' />
                </SkeletonRow>
                {[0, 1, 2].map((index) => (
                    <SkeletonStack key={index} $gap='12px'>
                        <SkeletonBlock $width='180px' $height='20px' />
                        <SkeletonBlock $height='180px' $radius='8px' />
                    </SkeletonStack>
                ))}
                <SkeletonRow $gap='12px'>
                    <SkeletonBlock $width='100px' $height='40px' />
                    <SkeletonBlock $width='140px' $height='40px' />
                </SkeletonRow>
            </SkeletonStack>
        </SkeletonPageShell>
    );
};
